import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  PageHeader,
  FilterBar,
  EmptyState,
  StatusChip,
} from "../components/ui";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { contentAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import useListQueryParams from "../hooks/useListQueryParams";
import AdminDateRangeFilter from "../components/AdminDateRangeFilter.jsx";
import { statusChipColors } from "../theme";
import "./ModuleAdmin.css";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "contacted", label: "Contacted" },
  { value: "closed", label: "Closed" },
];

function formatDate(createdAt) {
  if (!createdAt) return "—";
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const VolunteerInquiries = () => {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { showToast } = useToast();
  const canManage = can("volunteer_inquiries_manage");
  const { query, setQuery } = useListQueryParams({
    page: "1",
    limit: "20",
    status: "",
    search: "",
    fromDate: "",
    toDate: "",
    sortOrder: "desc",
  });

  const page = Number(query.page || 1);
  const limit = Number(query.limit || 20);
  const statusFilter = query.status || "";
  const search = query.search || "";
  const fromDate = query.fromDate || "";
  const toDate = query.toDate || "";
  const sortOrder = query.sortOrder || "desc";

  const { data, isLoading } = useQuery(
    [
      "volunteer-inquiries",
      statusFilter,
      search,
      fromDate,
      toDate,
      sortOrder,
      page,
      limit,
    ],
    () =>
      contentAPI.getVolunteerInquiries({
        page,
        limit,
        status: statusFilter || undefined,
        search: search || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        sortOrder,
      }),
  );

  const requests = data?.data?.data || [];
  const pagination = data?.data?.pagination || {
    page: 1,
    totalPages: 1,
    total: requests.length,
  };

  const updateStatusMutation = useMutation(
    ({ id, status }) => contentAPI.updateVolunteerInquiry(id, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("volunteer-inquiries");
        showToast({
          message: "Volunteer inquiry status updated.",
          severity: "success",
        });
      },
      onError: (err) =>
        showToast({
          message: err.response?.data?.message || "Failed to update status",
          severity: "error",
        }),
    },
  );

  const handleDownload = async () => {
    try {
      const res = await contentAPI.getVolunteerInquiries({
        limit: 10000,
        status: statusFilter || undefined,
        search: search || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        sortOrder,
      });
      const allData = res.data?.data || [];
      if (!allData.length) {
        showToast({ message: "No data to download", severity: "warning" });
        return;
      }

      const headers = ["Name", "Phone", "Date", "Status"];
      const csv = [
        headers.join(","),
        ...allData.map((row) =>
          [
            '"' + (row.name || "").replace(/"/g, '""') + '"',
            '"' + (row.phone || "").replace(/"/g, '""') + '"',
            '"' + formatDate(row.createdAt) + '"',
            '"' + (row.status || "pending") + '"',
          ].join(","),
        ),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `volunteer-inquiries-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast({ message: "Failed to download data", severity: "error" });
    }
  };

  return (
    <div className="module-page">
      <PageHeader
        title="Volunteer Inquiries"
        subtitle='Submissions from the About page "Join Invincible as Volunteer" popup.'
      />

      <FilterBar>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setQuery({ status: e.target.value })}
          >
            <MenuItem value="">All</MenuItem>
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search name or phone"
          value={search}
          onChange={(e) => setQuery({ search: e.target.value })}
          sx={{ minWidth: 260 }}
        />
        <AdminDateRangeFilter
          startDate={fromDate}
          endDate={toDate}
          onStartDateChange={(v) => setQuery({ fromDate: v })}
          onEndDateChange={(v) => setQuery({ toDate: v })}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Sort</InputLabel>
          <Select
            value={sortOrder}
            label="Sort"
            onChange={(e) => setQuery({ sortOrder: e.target.value })}
          >
            <MenuItem value="desc">Latest first</MenuItem>
            <MenuItem value="asc">Oldest first</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<DownloadRoundedIcon />}
          onClick={handleDownload}
          sx={{ height: 40 }}
        >
          Export
        </Button>
      </FilterBar>

      <div className="module-card">
        {isLoading ? (
          <p className="module-loading">Loading volunteer inquiries…</p>
        ) : requests.length === 0 ? (
          <EmptyState
            title="No volunteer inquiries yet"
            description="Submissions from the volunteer popup will appear here."
          />
        ) : (
          <table className="module-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((row) => (
                <tr key={row.id}>
                  <td>{row.name || "—"}</td>
                  <td>{row.phone || "—"}</td>
                  <td>{formatDate(row.createdAt)}</td>
                  <td>
                    {canManage ? (
                      <Select
                        size="small"
                        value={row.status || "pending"}
                        disabled={updateStatusMutation.isLoading}
                        onChange={(e) =>
                          updateStatusMutation.mutate({
                            id: row.id,
                            status: e.target.value,
                          })
                        }
                        sx={{
                          minWidth: 125,
                          backgroundColor:
                            statusChipColors[row.status]?.bg || "#f1f5f9",
                          color:
                            statusChipColors[row.status]?.color || "#475569",
                          borderRadius: "16px",
                          fontWeight: 600,
                          fontSize: "0.80rem",
                          height: "28px",
                          ".MuiOutlinedInput-notchedOutline": {
                            borderColor:
                              statusChipColors[row.status]?.border || "#cbd5e1",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor:
                              statusChipColors[row.status]?.border || "#cbd5e1",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor:
                              statusChipColors[row.status]?.border || "#cbd5e1",
                          },
                          ".MuiSelect-select": {
                            paddingTop: "2px",
                            paddingBottom: "2px",
                            paddingLeft: "10px",
                          },
                          ".MuiSvgIcon-root": {
                            color:
                              statusChipColors[row.status]?.color || "#475569",
                          },
                        }}
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <StatusChip
                        status={row.status || "pending"}
                        label={
                          STATUS_OPTIONS.find((o) => o.value === row.status)
                            ?.label ||
                          row.status ||
                          "Pending"
                        }
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {pagination.totalPages > 1 && (
          <div className="module-list-pagination">
            <span>
              Page {pagination.page} of {pagination.totalPages} (
              {pagination.total} total)
            </span>
            <div className="module-list-pagination-btns">
              <button
                type="button"
                className="module-btn secondary"
                disabled={page <= 1}
                onClick={() =>
                  setQuery({ page: String(page - 1) }, { resetPage: false })
                }
              >
                Previous
              </button>
              <button
                type="button"
                className="module-btn secondary"
                disabled={page >= pagination.totalPages}
                onClick={() =>
                  setQuery({ page: String(page + 1) }, { resetPage: false })
                }
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VolunteerInquiries;
